import { useState, useCallback, useMemo } from 'react';
import type { ISODateString, PeriodMonth } from '../types/timesheet.types';
import {
  getCurrentWeekStart,
  addWeeks,
  formatWeekLabel,
  getPeriodMonth,
  getWeekStart,
  parseISODate,
  addMonths,
  getCalendarWeeksForMonth,
  formatMonthLabel,
} from '../utils/dateUtils';

interface UseWeekNavigationResult {
  weekStart: ISODateString;
  periodMonth: PeriodMonth;
  /** Explicitly selected month — use this for data loading and OOM checks. */
  selectedMonth: PeriodMonth;
  calendarMonth: string;
  weekLabel: string;
  /** "April 2026" — label for the selected month. */
  monthLabel: string;
  goToPrevWeek: () => void;
  goToNextWeek: () => void;
  /** Navigate to the previous month, landing on its first calendar week. */
  goToPrevMonth: () => void;
  /** Navigate to the next month, landing on its first calendar week. */
  goToNextMonth: () => void;
  /** Navigate to the week containing the given date. Does not change selectedMonth. */
  goToWeek: (date: Date | ISODateString) => void;
  goToCurrentWeek: () => void;
  isCurrentWeek: boolean;
}

function monthFromWeekStart(ws: ISODateString): PeriodMonth {
  const wed = parseISODate(ws);
  wed.setDate(wed.getDate() + 2);
  return getPeriodMonth(wed);
}

export function useWeekNavigation(initialWeekStart?: ISODateString): UseWeekNavigationResult {
  const [weekStart, setWeekStart] = useState<ISODateString>(
    () => initialWeekStart ?? getCurrentWeekStart()
  );

  // Wednesday (weekStart + 2) always falls in the majority-working-days month
  // for any cross-month boundary week, so it's the right anchor for period context.
  const periodMonth = useMemo(() => monthFromWeekStart(weekStart), [weekStart]);

  // Explicit selected month — driven by month navigation buttons, not derived from week.
  const [selectedMonth, setSelectedMonth] = useState<PeriodMonth>(
    () => monthFromWeekStart(initialWeekStart ?? getCurrentWeekStart())
  );

  const weekLabel = useMemo(() => formatWeekLabel(weekStart), [weekStart]);
  const monthLabel = useMemo(() => formatMonthLabel(selectedMonth), [selectedMonth]);

  const calendarMonth = useMemo(() => {
    const date = parseISODate(weekStart);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }, [weekStart]);

  const goToPrevWeek = useCallback(() => {
    setWeekStart(s => addWeeks(s, -1));
  }, []);

  const goToNextWeek = useCallback(() => {
    setWeekStart(s => addWeeks(s, 1));
  }, []);

  const goToPrevMonth = useCallback(() => {
    setSelectedMonth(pm => {
      const prev = addMonths(pm, -1);
      const weeks = getCalendarWeeksForMonth(prev);
      if (weeks.length > 0) setWeekStart(weeks[0].weekStart);
      return prev;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setSelectedMonth(pm => {
      const next = addMonths(pm, 1);
      const weeks = getCalendarWeeksForMonth(next);
      if (weeks.length > 0) setWeekStart(weeks[0].weekStart);
      return next;
    });
  }, []);

  // goToWeek only changes weekStart; selectedMonth is controlled by month nav buttons.
  const goToWeek = useCallback((date: Date | ISODateString) => {
    setWeekStart(getWeekStart(date));
  }, []);

  const goToCurrentWeek = useCallback(() => {
    const ws = getCurrentWeekStart();
    setWeekStart(ws);
    setSelectedMonth(monthFromWeekStart(ws));
  }, []);

  return {
    weekStart,
    periodMonth,
    selectedMonth,
    calendarMonth,
    weekLabel,
    monthLabel,
    goToPrevWeek,
    goToNextWeek,
    goToPrevMonth,
    goToNextMonth,
    goToWeek,
    goToCurrentWeek,
    isCurrentWeek: weekStart === getCurrentWeekStart(),
  };
}
